/**
 * Filter helper utilities for NewEntityFilters component
 * Extracted from the main component to improve modularity and testability
 */
import { ACTIVITIES, OPPORTUNITIES } from 'constants/Entities';
import { getRealID } from 'utils/filters';

/**
 * @param {string} filterId
 * @param {string[]} readOnlyQuickFilters
 * @returns {boolean}
 */
export const isReadOnlyQuickFilterId = (filterId, readOnlyQuickFilters = []) => {
    if (!filterId || !readOnlyQuickFilters?.length) return false;
    const bareId = getRealID(filterId);
    return readOnlyQuickFilters.includes(filterId) || readOnlyQuickFilters.includes(bareId);
};

/**
 * Read-only quick filters are always present in the quick filters bar (prepended, deduped).
 * @param {string[]} quickFilters
 * @param {string[]} readOnlyQuickFilters
 * @returns {string[]}
 */
export const mergeReadOnlyQuickFilters = (quickFilters = [], readOnlyQuickFilters = []) => {
    if (!readOnlyQuickFilters?.length) return [...quickFilters];
    return [...new Set([...readOnlyQuickFilters, ...quickFilters])];
};

/**
 * Gets fixed visible filters based on entity type
 * @param {Object} entity - Entity configuration
 * @returns {Array} Array of fixed visible filter IDs
 */
const getFixedVisibleFilters = (entity) => {
    let visible = [];
    if (entity.trueName === ACTIVITIES.trueName) {
        visible.push('isMarketingEmail');
    }
    return visible;
};

/**
 * Applies quick filters overrides based on entity type
 * @param {Array} quickFilters - Array of quick filter IDs
 * @param {Object} entity - Entity configuration
 * @returns {Array} Modified quick filters array
 */
const applyQuickFiltersOverrides = (quickFilters, entity) => {
    const modifiedFilters = [...quickFilters];
    if (!modifiedFilters.includes('state') && entity.trueName === OPPORTUNITIES.trueName) {
        modifiedFilters.unshift('state');
    }
    return modifiedFilters;
};

/**
 * Gets cached state configuration
 * @param {Object} config - Configuration object
 * @param {Object} config.cache - Cached filter state
 * @param {Array} config.defaultQuickFilters - Default quick filters
 * @param {Array} config.defaultFilters - Default filters
 * @param {Object} config.entity - Entity configuration
 * @param {string[]} [config.readOnlyQuickFilters] - Always kept in quickFilters
 * @param {boolean} [config.restoreDefaultFiltersOnEmptyCache] - When true, empty cached filter lists fall back to defaults
 * @returns {Object} Cached state object
 */
export const getStateCached = ({
    cache,
    defaultQuickFilters,
    defaultFilters,
    entity,
    readOnlyQuickFilters = [],
    restoreDefaultFiltersOnEmptyCache = false,
}) => {
    let cachedState = null;
    let visibleFilters = [];
    let quickFilters = [];
    let openFilters = {};
    let openQuickFilters = {};
    let crossSchema = {};

    const initialCache = cache;

    if (initialCache) {
        quickFilters = initialCache.hasOwnProperty('quickFilters')
            ? initialCache.quickFilters || []
            : defaultQuickFilters;

        quickFilters = applyQuickFiltersOverrides(quickFilters, entity);
        quickFilters = mergeReadOnlyQuickFilters(quickFilters, readOnlyQuickFilters);

        // All quick filters have to be visible for consistency
        visibleFilters = new Set([
            ...(initialCache.visibleFilters || []),
            ...quickFilters,
            ...readOnlyQuickFilters,
            ...getFixedVisibleFilters(entity),
        ]);

        visibleFilters = Array.from(visibleFilters);
        visibleFilters = visibleFilters.filter(
            (filter) => typeof filter === 'string' || filter instanceof String,
        );

        openFilters = cache.openFilters || {};
        openQuickFilters = cache.openQuickFilters || {};
        crossSchema = cache.crossSchema || {};

        if (restoreDefaultFiltersOnEmptyCache && !visibleFilters.length) {
            const initialQuickFilters = mergeReadOnlyQuickFilters(
                defaultQuickFilters,
                readOnlyQuickFilters,
            );
            cachedState = {
                visibleFilters: [
                    ...new Set([
                        ...defaultFilters,
                        ...initialQuickFilters,
                        ...readOnlyQuickFilters,
                        ...getFixedVisibleFilters(entity),
                    ]),
                ],
                openFilters,
                openQuickFilters,
                quickFilters: initialQuickFilters,
                crossSchema,
            };
        } else {
            cachedState = {
                visibleFilters,
                openFilters,
                openQuickFilters,
                quickFilters,
                crossSchema,
            };
        }
    } else {
        const initialQuickFilters = mergeReadOnlyQuickFilters(
            defaultQuickFilters,
            readOnlyQuickFilters,
        );
        cachedState = {
            visibleFilters: [
                ...new Set([...defaultFilters, ...initialQuickFilters, ...readOnlyQuickFilters]),
            ],
            openFilters,
            openQuickFilters,
            quickFilters: initialQuickFilters,
            crossSchema,
        };
    }

    return cachedState;
};

/**
 * Maps schema array to object with fieldId and fieldConfiguration keys
 * @param {Array} schema - Schema array
 * @returns {Object} Mapped schema object
 */
export const getMappedSchema = (schema) => {
    return schema.reduce((obj, current) => {
        if (!obj.schemaByFieldId) obj.schemaByFieldId = {};
        if (!obj.schemaByFieldConfiguration) obj.schemaByFieldConfiguration = {};
        obj.schemaByFieldId[current.id] = current;
        if (current.fieldConfiguration) {
            obj.schemaByFieldConfiguration[current.fieldConfiguration] = current;
        }
        return obj;
    }, {});
};

/**
 * Gets extra filters schema configuration
 * @param {Object} config - Configuration object
 * @param {Array} config.schema - Filter schema array
 * @param {Array} config.extraSchema - Extra schema configuration
 * @param {Array} config.hiddenFilters - Hidden filters array
 * @param {Object} config.entity - Entity configuration
 * @param {Array<string>} [config.readOnlyFilterIds] - Filter ids with readOnlySelection in add-filters modal (can check, cannot uncheck)
 * @returns {Array} Extra filters schema
 */
export const getExtraFiltersSchema = ({
    schema,
    extraSchema,
    hiddenFilters = [],
    entity,
    readOnlyFilterIds = [],
}) => {
    const options = schema.reduce((filters, filter) => {
        let isStaticFilter = false;

        if (filter.id === 'pendingConversations' && entity === ACTIVITIES.trueName) {
            isStaticFilter = true;
        } else if (filter.id === 'state' && entity === OPPORTUNITIES.trueName) {
            isStaticFilter = true;
        }

        if (!hiddenFilters?.includes(filter.id)) {
            const isReadOnlySelection = readOnlyFilterIds.includes(filter.id);
            filters.push({
                id: filter.id,
                label: filter.description,
                forceDisabled: isStaticFilter,
                ...(isReadOnlySelection ? { readOnlySelection: true } : {}),
            });
        }
        return filters;
    }, []);

    let groupedOptions = [];

    if (extraSchema && extraSchema.length > 0) {
        const mappedOptions = options.reduce((obj, current) => {
            obj[current.id] = current;
            return obj;
        }, {});

        groupedOptions = extraSchema.reduce((result, current) => {
            let group = {
                label: current.label,
                id: current.label,
            };

            let groupOptions = current.options.reduce((arr, option) => {
                if (mappedOptions[option]) {
                    arr.push(mappedOptions[option]);
                    mappedOptions[option].included = true;
                }
                return arr;
            }, []);

            group.options = groupOptions;
            result.push(group);

            return result;
        }, []);
    }

    let finalOptions = options;
    if (groupedOptions && groupedOptions.length > 0) finalOptions = groupedOptions;

    return finalOptions;
};
